import { Component, OnInit } from '@angular/core';
import { AddMessageConversationEntryResponse_Outcome, CloseTicketRequest, CloseTicketResponse_Outcome, DetailedTicket, GetTicketAttachmentDownloadUrlRequest, GetTicketDetailRequest, GetTicketDetailResponse_Outcome, PersistedTicketAttachment, TicketState } from '@proto/hubspotr';
import { Subscription } from 'src/app/subscription/subscription.model';
import { SupportV2Service } from '../supportv2.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SubscriptionService } from 'src/app/subscription/subscription.service';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { AddMessageConversationEntryRequest, PreparedTicketAttachment, TicketRequest } from '@proto/mypascom';
import { Model } from 'src/app/app.model';
import { ToastService } from 'src/app/toast.service';
import { AttachedFile } from 'src/app/support/support.model';
import { AssetService } from 'src/app/asset.service';
import { AutorunComponent } from '@myshared/autorun.component';
import { I18NextService } from 'angular-i18next';

@Component({
  selector: 'app-ticket-details-v2',
  templateUrl: './ticket-details-v2.component.html',
  standalone: false
})
export class TicketDetailsV2Component extends AutorunComponent implements OnInit {

  public applianceId: number = 0;
  public ticket: DetailedTicket;

  public message: string;
  public isRequesting = false;
  public isUploading = false;
  public attachedFiles: AttachedFile[] = [];
  public preparedAttachments: PreparedTicketAttachment[] = [];
  public isTicketView = true;
  public subscription: Subscription;
  public isLink = false;

  constructor(public supportService: SupportV2Service,
    private subscriptionService: SubscriptionService,
    private route: ActivatedRoute,
    private router: Router,
    private m: Model,
    private toastService: ToastService,
    private i18Next: I18NextService,
    private assetService: AssetService) {
    super();
  }

  async ngOnInit() {

    const threadId = this.route.snapshot.paramMap.get('id');
    await this.getTicketDetails(threadId ?? '');

    this.subscriptionService.refresh();
    this.autorun(() => {
      if (this.ticket) {
        const subscription = this.m.subscription.subscriptions.find((sub: Subscription) => sub.name === this.ticket.applianceName);
        this.subscription = subscription ?? new Subscription();
        this.isLink = !!subscription;
      } else {
        this.subscription = new Subscription();
      }
    })
  }

  async getTicketDetails(threadId: string) {
    const getTicketDetailRequest = GetTicketDetailRequest.create({
      threadId: threadId
    });
    const ticketDetailsReq = TicketRequest.create({
      request: {
        $case: 'getTicketDetailRequest',
        getTicketDetailRequest: getTicketDetailRequest
      }
    });

    try {
      const ticketDetailResponse = await firstValueFrom(this.supportService.getTicketDetails(ticketDetailsReq));
      const result = ticketDetailResponse.outcome;

      if (result === GetTicketDetailResponse_Outcome.OUTCOME_SUCCESS) {
        this.ticket = ticketDetailResponse.detailedTicket;
      }
      if (result === GetTicketDetailResponse_Outcome.OUTCOME_ERROR) {
        this.toastService.error(this.i18Next.t('support_ticket_get_details_error'), 'error');
        this.router.navigate(['/support']);
      }
    } catch (error) {
      this.toastService.error(this.i18Next.t('support_ticket_get_details_error'), 'error');
      this.router.navigate(['/support']);
    }
  }

  get isTicketClosed(): boolean {
    return this.ticket.state === TicketState.STATE_CLOSED;
  }

  get isTicketClosable(): boolean {
    return this.ticket.state !== TicketState.STATE_CLOSED && this.ticket.state !== TicketState.STATE_WAIT_ON_DEV;
  }

  closeTicket() {
    this.isTicketView = false;
  }

  onCloseCancel() {
    this.isTicketView = true;
  }

  async onCloseConfirm() {
    if (this.isRequesting) {
      return;
    }
    this.isTicketView = true;
    this.isRequesting = true;

    const closeTicketRequest = CloseTicketRequest.create({
      threadId: this.ticket.threadId,
    });
    const closeTicketReq = TicketRequest.create({
      request: {
        $case: 'closeTicketRequest',
        closeTicketRequest: closeTicketRequest
      }
    });

    try {
      const closeTicketResponse = await firstValueFrom(this.supportService.closeTicket(closeTicketReq));
      this.isRequesting = false;
      const result = closeTicketResponse.outcome;

      if (result === CloseTicketResponse_Outcome.OUTCOME_SUCCESS) {
        this.getTicketDetails(this.ticket.threadId);
      }
      if (result === CloseTicketResponse_Outcome.OUTCOME_ERROR) {
        this.toastService.error(this.i18Next.t('support_ticket_close_error'), 'error');
      }
    } catch (error) {
      this.isRequesting = false;
      this.toastService.error(this.i18Next.t('support_ticket_close_error'), 'error');
    }
  }

  async downloadAttachment(attachment: PersistedTicketAttachment) {
    const getTicketAttachmentDownloadUrlRequest = GetTicketAttachmentDownloadUrlRequest.create({
      fileId: attachment.fileId,
      threadId: this.ticket.threadId
    });
    const getSignedDownloadUrlReq = TicketRequest.create({
      request: {
        $case: 'getTicketAttachmentDownloadUrlRequest',
        getTicketAttachmentDownloadUrlRequest: getTicketAttachmentDownloadUrlRequest
      }
    });

    try {
      const getAttachmentDownloadUrlResponse = await firstValueFrom(this.supportService.getAttachmentDownloadUrl(getSignedDownloadUrlReq));
      const url = getAttachmentDownloadUrlResponse.downloadUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        this.toastService.error(this.i18Next.t('support_ticket_download_attachment'), 'error');
      }
    } catch (error) {
      this.toastService.error(this.i18Next.t('support_ticket_download_attachment'), 'error');
    }
  }

  submitMessage() {
    if (!this.message || this.isRequesting || this.isUploading) {
      return;
    }
    this.startAttachmentUpload();
  }

  async startAttachmentUpload() {
    if (this.attachedFiles.length > 0) {
      this.isUploading = true;
      this.preparedAttachments = [];
      try {
        for (let f of this.attachedFiles) {
          const file = f.file;
          const assetData = await firstValueFrom(this.assetService.placeAsset(file.name, file.type));
          await lastValueFrom(this.assetService.upload(assetData.uploadUrl, file));
          const attachment = PreparedTicketAttachment.create({
            assetKey: assetData.assetKey,
            filename: file.name
          });
          this.preparedAttachments.push(attachment);
        }
        this.isUploading = false;
        this.addConversationEntry();
      } catch (error) {
        this.isUploading = false;
        this.toastService.error(this.i18Next.t('support_ticket_upload_files_error'), 'error');
      }
    } else {
      this.addConversationEntry();
    }
  }

  async addConversationEntry() {
    this.isRequesting = true;
    const addMessageConversationEntryRequest = AddMessageConversationEntryRequest.create({
      threadId: this.ticket.threadId,
      senderEmail: this.m.account.currentUser.email,
      body: this.message,
      attachments: this.preparedAttachments
    });
    const addMessageReq = TicketRequest.create({
      request: {
        $case: 'addMessageConversationEntryRequest',
        addMessageConversationEntryRequest: addMessageConversationEntryRequest
      }
    });

    try {
      const addMessageResponse = await firstValueFrom(this.supportService.addMessage(addMessageReq));
      this.isRequesting = false;
      const result = addMessageResponse.outcome;

      if (result === AddMessageConversationEntryResponse_Outcome.OUTCOME_SUCCESS) {
        this.message = '';
        this.getTicketDetails(this.ticket.threadId);
      }
      if (result === AddMessageConversationEntryResponse_Outcome.OUTCOME_ERROR) {
        this.toastService.error(this.i18Next.t('support_ticket_add_conversation_entry_error'), 'error');
      }
    } catch (error) {
      this.isRequesting = false;
      this.toastService.error(this.i18Next.t('support_ticket_add_conversation_entry_error'), 'error');
    }
  }

  selectedFilesChanged(attachedFiles: AttachedFile[]) {
    this.attachedFiles = attachedFiles;
  }

}
